import fp from 'lodash/fp';
import type { VisitConfig } from '@dynamika-shared/types/visits';
import type { CohortType } from '@dynamika-shared/types/cohorts';
import type {
  LabelMerged,
  ModalityLabel,
} from '@dynamika-shared/types/imagingLabels';
import {
  CommonInventoryType,
  UploadFileTypeEnum,
} from '@dynamika-shared/types/inventory';
import {
  QcCompletedStatuses,
  QcStatusEnum,
} from '@dynamika-shared/types/status';
import { ReaderTaskStatuses } from '@dynamika-shared/types/reader';

export const onlyDICOM = (value: CommonInventoryType) =>
  value.dataType === UploadFileTypeEnum.DICOM;

export const onlyFromConfig =
  (ids: string[] = []) =>
  (value: CommonInventoryType) =>
    ids.includes(value.id);

export const calcQCTaskCompleted = (status?: QcStatusEnum) => {
  return !!status && QcCompletedStatuses.includes(status);
};

export const calcReaderTaskCompleted = (status?: ReaderTaskStatuses) => {
  return status === 'COMPLETED' || status === 'FAILED';
};

/**
 *  Returns all DICOM labels from configuration flat (merged with modalityName)
 * @param labelGroups  - all DICOM labels from configuration
 * @returns {LabelMerged[]} - all DICOM labels from configuration flat (merged with modalityName)
 */
export const makeAllConfigLabelsFlat = (
  labelGroups: ModalityLabel[]
): LabelMerged[] =>
  fp.flatMap(({ modalityName, labels }) =>
    fp.map(label => ({ ...label, modalityName }), labels)
  )(labelGroups);

/**
 *  Returns all DICOM labels or labels for visit located in context in cohorts
 * @param allConfigLabels - all DICOM labels from configuration flat (merged with modalityName)
 * @param configVisitLabels - labels for visit located in context in cohorts (ids)
 * @param visitDefinitionId - visitDefinitionId for visit located in context in cohorts
 * @returns {LabelMerged[]} - all DICOM labels or labels for visit located in context in cohorts
 */
export const getAllVisitLabels = (
  allConfigLabels: LabelMerged[],
  configVisitLabels: string[],
  visitDefinitionId?: string
) => {
  // if visitDefinitionId is not provided, return all DICOM labels
  // otherwise, labels for visit located in context in cohorts
  const predicates = visitDefinitionId
    ? [onlyDICOM, onlyFromConfig(configVisitLabels)]
    : [onlyDICOM];

  return fp.filter(fp.overEvery(predicates))(allConfigLabels);
};

/**
 *  Returns all visits with labels for visit located in context in cohorts
 * @param cohorts  - all cohorts
 * @returns {VisitConfig[]} - all visits with labels for visit located in context in cohorts
 */
export const extractAllConfigVisits = (cohorts: CohortType[]) =>
  cohorts.reduce(
    (acc, cohort) => [...acc, ...cohort.visits],
    [] as VisitConfig[]
  );
